import Button from '@amzn/awsui-components-core/button';
import React from 'react';
import { useHierarchyFilter } from 'src/components/star-grid/hooks/useHierarchyFilter';
import { useFilterDropdownPosition } from 'src/components/star-grid/hooks/useFilterDropdownPosition';
import { hierarchyScopedMembers } from 'src/components/star-grid/utils/hierarchyFilterRows';
import type { PnlHierarchyMember } from 'src/components/star-grid/starGridTypes';
import type { StarGridText } from 'src/components/star-grid/hooks/useStarGridText';
import MemberSubsetModal from 'src/components/star-grid/components/subset-editor/MemberSubsetModal';
import styles from 'src/components/star-grid/components/styles/HierarchyFilter.module.scss';
// The chip chrome — box, label, value slot — comes from [[ReportFilterField]], the SAME wrapper the
// non-hierarchical filters use.
//
// It used to be re-created here from the bar's classes, which meant the bar had two chip
// implementations. `ReportParameterFilter` picks between them on whether `hierarchy_id` has resolved
// yet, so a report visibly changed appearance mid-load: the first paint rendered the plain field and
// the swap to this control replaced the pill with bespoke markup. Sharing one wrapper makes the two
// states identical by construction rather than by keeping two stylesheets in step.
import { ReportFilterField } from 'src/components/star-grid/components/ReportFilterField';

/**
 * A tree row's indent.
 *
 * The per-level step is a CSS custom property rather than a literal so each bar can set its own — the
 * deck's popover steps ~13px where this control was built at 30px — without the row's depth, which
 * only this component knows, having to leave it. The fallback reproduces the original spacing exactly
 * for every existing caller.
 */
const rowIndent = (depth: number): React.CSSProperties => ({
  paddingInlineStart: `calc(18px + ${depth} * var(--hf-indent, 30px))`
});

interface Props {
  readonly id: string;
  readonly label: string;
  readonly members: readonly PnlHierarchyMember[];
  readonly values: readonly string[];
  readonly onChange: (values: readonly string[]) => void;
  readonly text: StarGridText;
  /** Offer only this member's subtree — what the report was created with. */
  readonly scopeRoot?: string | null;
  readonly multiple?: boolean;
  readonly disabled?: boolean;
  readonly loading?: boolean;
  /** Called when the panel opens, so the caller can load the tree only once someone wants it. */
  readonly onOpen?: () => void;
}

/**
 * A hierarchical filter: the pill shows the selection, the panel is the member tree.
 *
 * Generic on purpose — any report screen with a hierarchy (Product, Channel, Adjustment, Allocation,
 * Account, Cost Center) renders the same control, so the tree is the argument rather than the
 * component. Hierarchical members were previously picked in a modal AG Grid, which took the user off
 * the report to choose a filter value and could not show the parent chain inline.
 *
 * Multi-select: clicking a label toggles it and selected members read blue. Expansion is a separate
 * hit target (the chevron) so opening a parent never changes the selection — in a tree those are two
 * different intentions and one click cannot mean both.
 */
export const HierarchyFilterDropdown: React.FC<Props> = ({
  id,
  label,
  members,
  values,
  onChange,
  text,
  scopeRoot,
  multiple = true,
  disabled,
  loading,
  onOpen
}) => {
  const scoped = React.useMemo(() => hierarchyScopedMembers(members, scopeRoot), [members, scopeRoot]);
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const filter = useHierarchyFilter({ members: scoped, values, multiple, onChange });
  // Stable identity so the modal's tree-sync effect (which now depends on `hierarchyTree`) re-runs on
  // genuine member changes, not on every render of this inline object.
  const memberSubsetTree = React.useMemo(() => ({ hierarchy_id: id, label, member_count: scoped.length, members: [...scoped] }), [id, label, scoped]);
  const marker = useFilterDropdownPosition(id);
  const panel = React.useRef<HTMLDivElement>(null);

  // Dismiss on outside click / Escape: the panel is viewport-fixed, so it is not clipped by — and
  // cannot rely on — the filter bar's own overflow handling.
  React.useEffect(() => {
    if (!filter.open) return;
    const onPointer = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!panel.current?.contains(target) && !document.getElementById(id)?.contains(target)) filter.setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') filter.setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [filter, id]);

  const summary = values.length === 0 ? text('hierarchyFilterAll') : values.length === 1 ? values[0] : `${values[0]} +${values.length - 1}`;

  // `dropdown={false}`: this control draws its own caret immediately after the value, which is where
  // the deck puts it, rather than pinned to the chip's right edge.
  return (
    <ReportFilterField label={label} controlId={id} value={summary} title={values.join(', ')} dropdown={false}>
      <button
        type="button"
        id={id}
        className={styles.trigger}
        disabled={disabled || loading}
        aria-haspopup="dialog"
        aria-expanded={filter.open}
        onClick={() => {
          if (!filter.open) onOpen?.();
          filter.setOpen(!filter.open);
        }}
        data-testid={`hierarchy-filter-${id}`}
      >
        <span className={styles.triggerValue}>{loading ? text('hierarchyFilterLoading') : summary}</span>
        <span className={styles.caret} aria-hidden="true" />
      </button>
      {filter.open && (
        <div className={styles.panel} role="dialog" aria-label={label} ref={panel}>
          <span className={styles.marker} ref={marker} aria-hidden="true" />
          <div className={styles.searchRow}>
            <span className={styles.searchIcon} aria-hidden="true" />
            <input
              className={styles.search}
              type="search"
              placeholder={text('hierarchyFilterSearch')}
              aria-label={text('hierarchyFilterSearch')}
              value={filter.search}
              onChange={(event) => filter.setSearch(event.target.value)}
            />
            {/* The stacked-rules affordance opens the SAME member picker the Studio uses, as an escape
                hatch: the inline tree is right for picking a few members, the grid is right for
                searching and bulk-selecting hundreds. Confirming there pulls the selection back into
                this filter rather than replacing it with a separate concept. */}
            {/* The member-picker trigger, as a real Cloudscape icon button.
                It was a hand-styled empty `<button>` drawing three bars from background gradients, and it
                resolved as HIDDEN in the browser — so the picker was unreachable from the chip and the
                only route left was the footer link. `list-view` is the closest stock icon to the three
                stacked rules the preview shows. */}
            <span title={text('hierarchyFilterOpenPicker')} className={styles.pickerTrigger}>
              <Button
                variant="icon"
                iconName="list-view"
                ariaLabel={text('hierarchyFilterOpenPicker')}
                onClick={() => setPickerOpen(true)}
                data-testid={`hierarchy-filter-picker-${id}`}
              />
            </span>
          </div>
          <div className={styles.tree} role="tree">
            {filter.rows.map((row) => (
              <div
                key={row.path}
                className={styles.row}
                role="treeitem"
                aria-selected={filter.selected.has(row.id)}
                aria-expanded={row.hasChildren ? row.expanded : undefined}
                style={rowIndent(row.depth)}
              >
                <button
                  type="button"
                  className={filter.selected.has(row.id) ? `${styles.member} ${styles.memberSelected}` : styles.member}
                  onClick={() => filter.toggleSelected(row.id)}
                >
                  {row.label}
                </button>
                {row.hasChildren && (
                  <button
                    type="button"
                    className={row.expanded ? `${styles.chevron} ${styles.chevronOpen}` : styles.chevron}
                    onClick={() => filter.toggleExpanded(row.path)}
                    aria-label={row.expanded ? text('hierarchyFilterCollapse') : text('hierarchyFilterExpand')}
                  />
                )}
              </div>
            ))}
            {filter.rows.length === 0 && <p className={styles.empty}>{text('hierarchyFilterNoMatches')}</p>}
          </div>
          {/* No picker link here: the icon button beside the search box is a real Cloudscape icon button
              now and opens the same modal, so a second worded route was redundant chrome. The footer is
              kept for the count and Clear, which only a `multiple` filter has. */}
          {multiple && (
            <div className={styles.footer}>
              <span>{text('hierarchyFilterSelectedCount').replace('{count}', String(values.length))}</span>
              <span className={styles.footerActions}>
                <button type="button" className={styles.clear} onClick={filter.clear} disabled={values.length === 0}>
                  {text('hierarchyFilterClear')}
                </button>
              </span>
            </div>
          )}
        </div>
      )}
      {pickerOpen && (
        <MemberSubsetModal
          visible
          hierarchyId={id}
          hierarchyTree={memberSubsetTree}
          dimensionLabel={label}
          multiple={multiple}
          requireSelection={!multiple}
          initialChecked={values}
          onConfirm={(members) => {
            onChange(members);
            setPickerOpen(false);
          }}
          onDismiss={() => setPickerOpen(false)}
          testId={`hierarchy-filter-members-${id}`}
        />
      )}
    </ReportFilterField>
  );
};
